import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import {
  isMobViewport,
  setupHallPageBodyBackground,
} from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import greenBtn from "../img/green_button.png";
import blurDesk from "../img/blur_Desk.jpg";
import blurMob from "../img/blur_mob.jpg";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "./hallTimer";
import {
  COMMENTARY_QUESTIONS,
  COMMENTARY_BOOTH_POINTS,
  COMMENTARY_BOOTH_UNLOCKS,
} from "./commentaryBoothData";
import { useCommentaryAudio } from "./useCommentaryAudio";
import { unlockHall1Hotspot, markHall1HotspotCompleted, getRedirectIfHall1StageDone } from "./medalDisplayData";
import titlePlate from "./stage2/title.png";
import panelBox from "./stage2/box.png";
import "../fonts/breuer-headline.css";
import "./stage2.css";

/** Large Illustrator SVGs — served from /public (embedded binary). */
const STAGE2_DESK_SVG = `${process.env.PUBLIC_URL || ""}/hall_1/stage2/stage2_desk.svg`;
const STAGE2_MOB_SVG = `${process.env.PUBLIC_URL || ""}/hall_1/stage2/stage2_mob.svg`;

const OPTION_LETTERS = ["A", "B", "C", "D"];

function useStage2SvgUrl(deskUrl, mobUrl) {
  const [svgUrl, setSvgUrl] = useState(() =>
    typeof window !== "undefined" && isMobViewport() ? mobUrl : deskUrl
  );

  useEffect(() => {
    const sync = () => {
      const next = isMobViewport() ? mobUrl : deskUrl;
      setSvgUrl((prev) => (prev === next ? prev : next));
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [deskUrl, mobUrl]);

  return svgUrl;
}

function syncPlayPauseVisibility(doc, isPlaying) {
  if (!doc) return;
  const playEl = doc.getElementById("play");
  const pauseEl = doc.getElementById("pause");
  if (playEl) {
    playEl.style.display = isPlaying ? "none" : "";
    playEl.style.visibility = isPlaying ? "hidden" : "visible";
  }
  if (pauseEl) {
    pauseEl.style.display = isPlaying ? "" : "none";
    pauseEl.style.visibility = isPlaying ? "visible" : "hidden";
  }
}

function styleSvgControl(el, { label }) {
  if (!el) return;
  el.style.pointerEvents = "auto";
  el.style.cursor = "pointer";
  el.setAttribute("role", "button");
  el.setAttribute("tabindex", "0");
  el.setAttribute("aria-label", label);
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

/** Hall Stage 2 — Commentary Booth MCQ puzzle */
export default function HallStage2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const audio = useCommentaryAudio();
  const stage2SvgUrl = useStage2SvgUrl(STAGE2_DESK_SVG, STAGE2_MOB_SVG);

  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(COMMENTARY_QUESTIONS.map((q) => [q.id, null]))
  );
  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [wrongPopup, setWrongPopup] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [svgReady, setSvgReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);
  const advanceTimerRef = useRef(null);
  const objectRef = useRef(null);
  const audioRef = useRef(audio);

  const currentQ = COMMENTARY_QUESTIONS[qIndex];
  const isLastQuestion = qIndex >= COMMENTARY_QUESTIONS.length - 1;

  const liveStagePoints = useMemo(() => {
    const correctCount = COMMENTARY_QUESTIONS.filter(
      (q) => answers[q.id] === q.correctOptionId
    ).length;
    return liveProgressPoints(
      correctCount,
      COMMENTARY_QUESTIONS.length,
      COMMENTARY_BOOTH_POINTS
    );
  }, [answers]);

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;
  const mcqLocked = !audio.hasPlayedOnce;

  useEffect(() => {
    audioRef.current = audio;
  }, [audio]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall1-stage2-page");
    const cleanupBg = setupHallPageBodyBackground(document.body, {
      deskForeground: blurDesk,
      mobForeground: blurMob,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall1-stage2-page");
      if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall1StageDone(stage, 2);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const completeStage = useCallback(
    async (finalAnswers) => {
      if (status === "success" || status === "saving" || completingRef.current) {
        return;
      }

      completingRef.current = true;
      setErrorMsg("");
      setStatus("saving");

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const payload = {
        puzzle: "commentary_booth",
        answers: finalAnswers,
      };

      try {
        let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
        if (!stageId && !isDemoBypass && backendBase && adminToken) {
          stageId = await ensureStageRowId({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
          });
          if (stageId) setStageRowId(String(stageId));
        }

        if (!isDemoBypass && backendBase && adminToken && stageId) {
          const result = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: 2,
            score: COMMENTARY_BOOTH_POINTS,
            answers: payload,
            time: timeStr,
          });
          if (result?.total_score != null) {
            setTotalScore(Number(result.total_score) || 0);
          } else {
            setTotalScore((prev) => prev + COMMENTARY_BOOTH_POINTS);
          }
        } else {
          setTotalScore((prev) => prev + COMMENTARY_BOOTH_POINTS);
        }

        markHall1HotspotCompleted("commentary_booth");
        unlockHall1Hotspot(COMMENTARY_BOOTH_UNLOCKS);
        setStatus("success");
      } catch (err) {
        console.error("Commentary booth save:", err);
        completingRef.current = false;
        setStatus("playing");
        setErrorMsg("Could not save progress. Please try again.");
      }
    },
    [status, stageRowId, isDemoBypass, backendBase, adminToken, storedUser]
  );

  const selectAnswer = useCallback(
    (questionId, optionId) => {
      if (
        mcqLocked ||
        status === "saving" ||
        status === "success" ||
        wrongPopup ||
        advanceTimerRef.current
      ) {
        return;
      }

      const question = COMMENTARY_QUESTIONS.find((q) => q.id === questionId);
      if (!question) return;

      const nextAnswers = { ...answers, [questionId]: optionId };
      setAnswers(nextAnswers);
      setErrorMsg("");

      if (optionId !== question.correctOptionId) {
        setWrongPopup(true);
        return;
      }

      if (isLastQuestion) {
        completeStage(nextAnswers);
        return;
      }

      advanceTimerRef.current = window.setTimeout(() => {
        advanceTimerRef.current = null;
        setQIndex((i) => Math.min(i + 1, COMMENTARY_QUESTIONS.length - 1));
      }, 350);
    },
    [
      mcqLocked,
      status,
      wrongPopup,
      answers,
      isLastQuestion,
      completeStage,
    ]
  );

  const dismissWrongPopup = useCallback(() => {
    setWrongPopup(false);
    setAnswers((prev) => ({ ...prev, [currentQ.id]: null }));
  }, [currentQ.id]);

  const toggleCommentary = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.isPlaying) a.pause();
    else a.play();
  }, []);

  const bindSvgControls = useCallback(() => {
    const obj = objectRef.current;
    if (!obj) return;
    let doc = null;
    try {
      doc = obj.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc) return;

    const svg = doc.querySelector("svg");
    if (svg) {
      svg.style.width = "100%";
      svg.style.height = "100%";
      svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
    }

    const bg = doc.getElementById("bg");
    if (bg) bg.style.pointerEvents = "none";

    const radio = doc.getElementById("radio");
    if (radio) radio.style.pointerEvents = "none";

    let playEl = doc.getElementById("play");
    let pauseEl = doc.getElementById("pause");

    const wireControl = (el, onActivate) => {
      if (!el?.parentNode) return null;
      const fresh = el.cloneNode(true);
      el.parentNode.replaceChild(fresh, el);
      fresh.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onActivate();
      });
      fresh.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onActivate();
        }
      });
      return fresh;
    };

    playEl = wireControl(playEl, () => {
      audioRef.current?.play();
      syncPlayPauseVisibility(doc, true);
    });
    pauseEl = wireControl(pauseEl, () => {
      audioRef.current?.pause();
      syncPlayPauseVisibility(doc, false);
    });

    styleSvgControl(playEl, { label: "Play commentary" });
    styleSvgControl(pauseEl, { label: "Pause commentary" });

    // Fallback: whole radio toggles if play/pause layers are missing
    if (!playEl && !pauseEl && radio) {
      const freshRadio = radio.cloneNode(true);
      radio.parentNode?.replaceChild(freshRadio, radio);
      freshRadio.style.pointerEvents = "auto";
      freshRadio.style.cursor = "pointer";
      freshRadio.setAttribute("role", "button");
      freshRadio.setAttribute("tabindex", "0");
      freshRadio.setAttribute("aria-label", "Play or pause commentary");
      const onToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleCommentary();
      };
      freshRadio.addEventListener("click", onToggle);
      freshRadio.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") onToggle(e);
      });
    }

    syncPlayPauseVisibility(doc, Boolean(audioRef.current?.isPlaying));
    setSvgReady(true);
  }, [toggleCommentary]);

  useEffect(() => {
    setSvgReady(false);
    const obj = objectRef.current;
    if (!obj) return undefined;

    if (obj.contentDocument?.querySelector("svg")) {
      bindSvgControls();
    }

    obj.addEventListener("load", bindSvgControls);
    return () => {
      obj.removeEventListener("load", bindSvgControls);
    };
  }, [bindSvgControls, stage2SvgUrl]);

  useEffect(() => {
    if (!svgReady) return;
    syncPlayPauseVisibility(
      objectRef.current?.contentDocument,
      audio.isPlaying
    );
  }, [audio.isPlaying, svgReady]);

  return (
    <div className="hall1-stage2 commentary-booth">
      <object
        key={stage2SvgUrl}
        ref={objectRef}
        className="commentary-booth__scene"
        data={stage2SvgUrl}
        type="image/svg+xml"
        aria-label="Commentary booth scene"
      >
        <p className="commentary-booth__scene-fallback">
          Unable to load commentary booth scene.
        </p>
      </object>

      {!svgReady ? (
        <p className="commentary-booth__scene-loading" aria-live="polite">
          Loading booth…
        </p>
      ) : null}

      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="commentary-booth__inner">
        <header className="commentary-booth__header">
          <div
            className="commentary-booth__title-plate"
            style={{ backgroundImage: `url(${titlePlate})` }}
          >
            <span className="commentary-booth__eyebrow"></span>
            <h1 className="commentary-booth__title">COMMENTARY BOOTH</h1>
          </div>
          <p className="commentary-booth__subtitle">
          Listen carefully to this iconic sporting moment, then answer the questions that follow.
          </p>
        </header>

        <div className="commentary-booth__stage">
          <aside
            className="commentary-panel commentary-panel--objective"
            style={{ backgroundImage: `url(${panelBox})` }}
          >
            <h2 className="commentary-panel__heading">OBJECTIVE</h2>
            <p className="commentary-panel__body">
              Listen to the commentary and answer the question based on the
              clue. Pay close attention to every word. Legends speak in details.
            </p>
          </aside>

          <div className="commentary-booth__center">
            <p className="commentary-booth__tap-hint">
              TAP PLAY TO HEAR THE COMMENTARY
            </p>

            {audio.loadError ? (
              <p className="commentary-booth__audio-note">{audio.loadError}</p>
            ) : null}
          </div>

          <aside
            className="commentary-panel commentary-panel--question"
            style={{ backgroundImage: `url(${panelBox})` }}
          >
            <h2 className="commentary-panel__heading">QUESTION</h2>
            <p className="commentary-panel__step">
              {qIndex + 1} / {COMMENTARY_QUESTIONS.length}
            </p>

            {mcqLocked ? (
              <p className="commentary-panel__locked" role="status">
                Press the play button to unlock the quiz.
              </p>
            ) : (
              <>
                <p className="commentary-panel__prompt">{currentQ.prompt}</p>
                <div
                  className="commentary-panel__options"
                  role="radiogroup"
                  aria-label={currentQ.prompt}
                >
                  {currentQ.options.map((opt, i) => {
                    const selected = answers[currentQ.id] === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={status === "saving" || wrongPopup}
                        className={`commentary-panel__option${
                          selected ? " commentary-panel__option--selected" : ""
                        }`}
                        onClick={() => selectAnswer(currentQ.id, opt.id)}
                      >
                        <span className="commentary-panel__letter">
                          {OPTION_LETTERS[i]}
                        </span>
                        <span className="commentary-panel__option-label">
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </aside>
        </div>

        <div className="commentary-booth__footer">
          <p className="commentary-booth__hint">
            <span className="commentary-booth__hint-icon" aria-hidden>
              💡
            </span>
            <span className="commentary-booth__hint-label">HINT</span>
            <span>Listen between the cheers.</span>
          </p>

          {errorMsg ? (
            <p className="commentary-booth__error" role="alert">
              {errorMsg}
            </p>
          ) : null}

          {status === "saving" ? (
            <p className="commentary-booth__saving" aria-live="polite">
              Saving…
            </p>
          ) : null}
        </div>
      </div>

      {wrongPopup ? (
        <div className="commentary-wrong" role="dialog" aria-modal="true">
          <div
            className="commentary-wrong__veil"
            aria-hidden
            onClick={dismissWrongPopup}
          />
          <div className="commentary-wrong__card">
            <h2 className="commentary-wrong__title">Incorrect</h2>
            <p className="commentary-wrong__text">
              That&apos;s not right. Listen again carefully and try another
              answer.
            </p>
            <button
              type="button"
              className="commentary-wrong__btn"
              onClick={dismissWrongPopup}
            >
              TRY AGAIN
            </button>
          </div>
        </div>
      ) : null}

      {status === "success" ? (
        <div className="commentary-success" role="dialog" aria-modal="true">
          <div className="commentary-success__veil" aria-hidden />
          <div className="commentary-success__card">
            <h2 className="commentary-success__title">Well played!</h2>
            <p className="commentary-success__text">
              The Champions&apos; Locker Archive is now unlocked.
            </p>
            <div className="commentary-success__stats">
              <span>{formatSecondsToClock(elapsedSec)}</span>
              <span>+{COMMENTARY_BOOTH_POINTS}</span>
            </div>
            <button
              type="button"
              className="commentary-booth__next"
              style={{ backgroundImage: `url(${greenBtn})` }}
              onClick={() => navigate("/hall-1")}
            >
              CONTINUE
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
